import React, {useEffect, useState} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';

interface ReportIssueDialogProps {
    open: boolean;
    submitting: boolean;
    onClose: () => void;
    onSubmit: (message: string) => void;
}

/** The generationId is attached server-side from the route, so the user never
 *  has to find or paste an id — the whole point of reporting from the item. */
const ReportIssueDialog: React.FC<ReportIssueDialogProps> = ({open, submitting, onClose, onSubmit}) => {
    const {t} = useTranslation();
    const [message, setMessage] = useState('');

    // A fresh box every time it opens: a stale draft from the previous item
    // would be attached to a different generation.
    useEffect(() => {
        if (open) setMessage('');
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>{t('main.creations.report.title')}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                    {t('main.creations.report.description')}
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={3}
                    // 2000 is the server's cap (GenerationReportBody.message);
                    // truncating here beats a 400 after the user typed an essay.
                    inputProps={{maxLength: 2000, 'aria-label': t('main.creations.report.placeholder')}}
                    placeholder={t('main.creations.report.placeholder')}
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    {t('main.creations.actions.cancel')}
                </Button>
                <Button variant="contained" disabled={submitting} onClick={() => onSubmit(message.trim())}>
                    {t('main.creations.report.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportIssueDialog;
